export function parseQuery(query: string): Record<string, string> {
  const parts = query.replace('?', '').split('&');
  const params = parts.reduce((acc: Record<string, string>, str: string) => {
    const [key, value] = str.split('=');
    acc[key] = value;
    return acc;
  }, {});
  return params;
}

export function removeQueryParams(...params: string[]) {
  const query = new URLSearchParams(window.location.search.replace('?', ''));
  params.forEach((param) => query.delete(param));
  history.pushState(null, '', window.location.pathname + query);
}

// Helper functions during development
export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function getSessionTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('_s');
  if (fromQuery) {
    return fromQuery;
  }

  const hash = window.location.hash.replace('#', '').trim();
  return hash || null;
}

export function getSessionBootstrapUrl(): string {
  const token = getSessionTokenFromUrl();
  if (token) {
    return `${window.location.origin}/api/sessions/request/${token}?redirect=true`;
  }

  return window.location.href;
}

export function extractDomainNameFromUrl(url: string) {
  const matches = url.match(/^https?\:\/\/([^\/:?#]+)(?:[\/:?#]|$)/i);
  return matches && matches[1]; // domain will be null if no match is found
}

/**
 * Drops leading/trailing empty rows and collapses accidental extra Enter hits
 * so at most one blank row remains between paragraphs.
 */
export function cleanTextFromTrailingEmptyRows(text: string): string {
  if (!text) {
    return '';
  }

  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/^[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * We cannot necessarily use the textarea itself to determine realistic row length for e.g. a PDF since it can get resized
 * Also we cannot simply count all the rows, because if some of the text is wrapped then we may end up with one mega row of e.g. 600 chars
 *
 * This function counts the number of rows by counting the split way,
 * and then also count the chars per row to determine if it in reality consists of more than one row
 *
 * Note that this is an approximation. It is not 100 % accurate, but should be good enough
 *
 * @param text
 * @param maxRowSize
 */
export function countRowsInTextArea(text: string, maxRowSize: number): number {
  let adjustedRowCount = 0;
  text += ''; // might not be a valid string

  const rows = text.split(/\r|\r\n|\n/);

  // clean-up end rows, so that we don't have ["wadhwa", "hhdwiad", "", ""], cause such rows shouldn't count
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i] === '') {
      rows.splice(i, 1);
    } else {
      break;
    }
  }

  for (const row of rows) {
    // if there is a completely new row then it must be counted
    adjustedRowCount++;

    const rowMultiplier = Math.ceil(row.length / maxRowSize);

    // if the row is very long (i.e. in reality multiple rows due to wrapping, count additional rows
    if (rowMultiplier >= 2) {
      adjustedRowCount += rowMultiplier - 1;
    }
  }

  return adjustedRowCount;
}

/**
 * Pixel budget for certificate v4 — keep in sync with
 * CertificateMessageLayout.java and certificate4.scala.html.
 * Shared band = card − header − footer(70) − logo(50+16, optional)
 * = message + gap(16) + names.
 * Names are pinned above the footer, or above the in-border logo.
 * Extra name lines grow upward and shrink the message budget.
 * Type shrinks 22→12 before we warn. Names stay at 18px until the
 * message (or a tall name stack) needs a smaller size, then both use
 * that size. Pad is always the 70px footer.
 */
export interface CertificateLayoutContext {
  hasPhoto: boolean;
  deceasedName: string;
  causeName: string;
  hasCauseLogo: boolean;
  senderName: string;
  hasIntermediaryLogo?: boolean;
}

export interface CertificateTextLayout {
  messageFontSize: string;
  senderFontSize: string;
  contentPadBottomPx: number;
  bodyHeightPx: number;
  mainRowHeightPx: number;
  senderRowHeightPx: number;
  message: string;
  donorNames: string;
  fits: boolean;
  messageBudgetPx: number;
  senderHeightPx: number;
  reservedBottomPx: number;
  senderOffsetFromBottomPx: number;
  messageLineHeight: string;
}

const CARD_INNER_HEIGHT_PX = 1034;
const PHOTO_HEIGHT_PX = 410;
const CONTENT_PAD_TOP_PX = 24;
const LABEL_HEIGHT_PX = 14;
const NAME_MARGIN_TOP_PX = 18;
const NAME_FONT_PX = 38;
const NAME_LINE_HEIGHT = 1.15;
const NAME_CHAR_WIDTH_EM = 0.55;
const DIVIDER_BLOCK_PX = 45;
const CAUSE_MARGIN_TOP_PX = 14;
const CAUSE_LOGO_PX = 36;
const CAUSE_FONT_PX = 22;
const CAUSE_LINE_HEIGHT = 1.15;
const CAUSE_CHAR_WIDTH_EM = 0.5;
const SENDER_OFFSET_FROM_BOTTOM_PX = 70;
const SENDER_FONT_PX = 18;
const SENDER_LINE_HEIGHT = 1.2;
const SENDER_CHAR_WIDTH_EM = 0.55;
const MESSAGE_COLUMN_PX = 710;
const MIN_GAP_ABOVE_SENDER_PX = 36;
const MIN_FONT_PX = 12;
const MIN_MESSAGE_RESERVE_PX = 80;
const FOOTER_RESERVE_PX = 24;
const DEFAULT_CONTENT_PAD_BOTTOM_PX = FOOTER_RESERVE_PX;
const MESSAGE_LINE_HEIGHT = 1.55;
const MESSAGE_CHAR_WIDTH_EM = 0.55;
const FONT_SIZES_PX = [22, 20, 18, 16, 15, 14, 13, 12];

function charsPerLine(fontPx: number, charWidthEm: number, minChars: number): number {
  return Math.max(minChars, Math.round(MESSAGE_COLUMN_PX / (fontPx * charWidthEm)));
}

function charsPerLineForFontSize(sizePx: number, charWidthEm = MESSAGE_CHAR_WIDTH_EM): number {
  return charsPerLine(sizePx, charWidthEm, 20);
}

function wrappedLines(text: string, fontPx: number, charWidthEm: number): number {
  if (!text || !text.trim()) {
    return 0;
  }
  return Math.max(1, countRowsInTextArea(text, charsPerLine(fontPx, charWidthEm, 10)));
}

function headerHeightPx(ctx: CertificateLayoutContext): number {
  const photo = ctx.hasPhoto ? PHOTO_HEIGHT_PX : 0;
  const nameLines = Math.max(1, wrappedLines(ctx.deceasedName, NAME_FONT_PX, NAME_CHAR_WIDTH_EM));
  const causeBlock = ctx.hasCauseLogo
    ? CAUSE_MARGIN_TOP_PX + CAUSE_LOGO_PX
    : CAUSE_MARGIN_TOP_PX +
      Math.ceil(
        Math.max(1, wrappedLines(ctx.causeName, CAUSE_FONT_PX, CAUSE_CHAR_WIDTH_EM)) *
          CAUSE_FONT_PX *
          CAUSE_LINE_HEIGHT
      );
  return (
    photo +
    CONTENT_PAD_TOP_PX +
    LABEL_HEIGHT_PX +
    NAME_MARGIN_TOP_PX +
    Math.ceil(nameLines * NAME_FONT_PX * NAME_LINE_HEIGHT) +
    DIVIDER_BLOCK_PX +
    LABEL_HEIGHT_PX +
    causeBlock +
    DIVIDER_BLOCK_PX
  );
}

function senderHeightFor(donorNames: string, fontPx: number, maxLines = Number.MAX_SAFE_INTEGER): number {
  if (!donorNames || !donorNames.trim()) {
    return 0;
  }
  const lines = Math.min(Math.max(1, wrappedLines(donorNames, fontPx, SENDER_CHAR_WIDTH_EM)), maxLines);
  return Math.ceil(lines * fontPx * SENDER_LINE_HEIGHT) + lines;
}

function clipToWrappedLines(text: string, fontPx: number, charWidthEm: number, maxLines: number): string {
  if (!text || !text.trim() || maxLines < 1) {
    return '';
  }
  const perLine = charsPerLine(fontPx, charWidthEm, 10);
  const rows = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const kept: string[] = [];
  let used = 0;
  for (const raw of rows) {
    if (used >= maxLines) {
      break;
    }
    let row = raw;
    const wraps = Math.max(1, Math.ceil(Math.max(row.length, 1) / perLine));
    const take = Math.min(wraps, maxLines - used);
    if (take < wraps) {
      row = row.substring(0, take * perLine);
    }
    kept.push(row);
    used += take;
  }
  return kept.join('\n');
}

function messageHeight(message: string, sizePx: number, lineHeight = MESSAGE_LINE_HEIGHT): number {
  if (!message || !message.trim()) {
    return 0;
  }
  const rows = countRowsInTextArea(message, charsPerLineForFontSize(sizePx));
  return Math.ceil(rows * sizePx * lineHeight) + 4;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function solveCertificateTextLayout(message: string, ctx: CertificateLayoutContext): CertificateTextLayout {
  const names = cleanTextFromTrailingEmptyRows(ctx.senderName || '');
  const greeting = cleanTextFromTrailingEmptyRows(message || '');
  const hasMessage = !!greeting;
  const hasNames = !!names;
  const header = headerHeightPx(ctx);
  const minMessage = hasMessage ? MIN_MESSAGE_RESERVE_PX : 0;
  const senderOffsetFromBottomPx = FOOTER_RESERVE_PX;
  const spaceBelowHeader = Math.max(0, CARD_INNER_HEIGHT_PX - header);
  const sharedBand = Math.max(0, spaceBelowHeader - senderOffsetFromBottomPx);
  let maxSenderHeight = Math.max(0, sharedBand - minMessage - MIN_GAP_ABOVE_SENDER_PX);

  if (hasNames && maxSenderHeight < senderHeightFor(names, MIN_FONT_PX, 1)) {
    maxSenderHeight = senderHeightFor(names, MIN_FONT_PX, 1);
  }

  let senderFontPx = SENDER_FONT_PX;
  let fittedNames = names;
  let senderHeight = 0;
  let namesClipped = false;
  let fittedMessage = greeting;
  let messageFont = `${FONT_SIZES_PX[0]}px`;
  let messageClipped = false;

  for (const sizePx of FONT_SIZES_PX) {
    let namesSize = hasNames ? Math.min(sizePx, SENDER_FONT_PX) : SENDER_FONT_PX;
    let trialNames = names;
    let trialSenderHeight = 0;
    let trialNamesClipped = false;

    if (hasNames) {
      const height = senderHeightFor(names, namesSize);
      if (height > maxSenderHeight) {
        if (sizePx > MIN_FONT_PX) {
          continue;
        }
        namesSize = MIN_FONT_PX;
        const maxLines = Math.max(1, Math.floor(maxSenderHeight / (namesSize * SENDER_LINE_HEIGHT)));
        trialNames = clipToWrappedLines(names, namesSize, SENDER_CHAR_WIDTH_EM, maxLines);
        trialSenderHeight = senderHeightFor(trialNames, namesSize, maxLines);
        trialNamesClipped = trialNames !== names;
      } else {
        trialSenderHeight = height;
      }
    }

    const namesBlock = hasNames ? trialSenderHeight + MIN_GAP_ABOVE_SENDER_PX : 0;
    const reservedBottomPx = clamp(senderOffsetFromBottomPx + namesBlock, 0, spaceBelowHeader);
    const messageBudget = Math.max(0, spaceBelowHeader - reservedBottomPx);
    let trialMessage = greeting;
    let trialMessageClipped = false;
    if (hasMessage && messageHeight(greeting, sizePx) > messageBudget) {
      if (sizePx > MIN_FONT_PX) {
        continue;
      }
      const maxLines = Math.max(0, Math.floor(messageBudget / (MIN_FONT_PX * MESSAGE_LINE_HEIGHT)));
      trialMessage = clipToWrappedLines(greeting, MIN_FONT_PX, MESSAGE_CHAR_WIDTH_EM, maxLines);
      trialMessageClipped = trialMessage !== greeting;
    }

    senderFontPx = namesSize;
    fittedNames = trialNames;
    senderHeight = trialSenderHeight;
    namesClipped = trialNamesClipped;
    fittedMessage = trialMessage;
    messageFont = `${sizePx}px`;
    messageClipped = trialMessageClipped;
    break;
  }

  const namesBlock = hasNames ? senderHeight + MIN_GAP_ABOVE_SENDER_PX : 0;
  const reservedBottomPx = clamp(senderOffsetFromBottomPx + namesBlock, 0, spaceBelowHeader);
  const contentPadBottomPx = FOOTER_RESERVE_PX;
  const messageBudget = Math.max(0, spaceBelowHeader - reservedBottomPx);

  const fontPx = parseInt(messageFont, 10);
  const messageLineHeight = String(MESSAGE_LINE_HEIGHT);
  const naturalMessageHeight = hasMessage ? messageHeight(fittedMessage, fontPx) : 0;
  const fittedMessageHeight = hasMessage
    ? Math.min(messageBudget, messageHeight(fittedMessage, fontPx, parseFloat(messageLineHeight)))
    : 0;
  // Warn only when the text cannot sit on the card at 12px — not when leftover
  // line-height fill rounds 1px over. Font shrink happens first (22→12).
  const messageFits = !hasMessage || naturalMessageHeight <= messageBudget;
  const withinCard = header + reservedBottomPx <= CARD_INNER_HEIGHT_PX;
  const inFlowFits = header + fittedMessageHeight + reservedBottomPx <= CARD_INNER_HEIGHT_PX;
  const bodyHeightPx = CARD_INNER_HEIGHT_PX - (ctx.hasPhoto ? PHOTO_HEIGHT_PX : 0);
  const senderRowHeightPx = hasNames ? senderHeight + MIN_GAP_ABOVE_SENDER_PX : 0;
  const mainRowHeightPx = Math.max(0, bodyHeightPx - senderRowHeightPx - senderOffsetFromBottomPx);

  return {
    messageFontSize: messageFont,
    senderFontSize: `${senderFontPx}px`,
    contentPadBottomPx,
    bodyHeightPx,
    mainRowHeightPx,
    senderRowHeightPx,
    message: fittedMessage,
    donorNames: fittedNames,
    fits: !namesClipped && !messageClipped && messageFits && withinCard && inFlowFits,
    messageBudgetPx: messageBudget,
    senderHeightPx: senderHeight,
    reservedBottomPx,
    senderOffsetFromBottomPx,
    messageLineHeight,
  };
}

export function availableCertificateMessageHeightPx(ctx: CertificateLayoutContext): number {
  return solveCertificateTextLayout('', ctx).messageBudgetPx;
}

export function maxCertificateMessageLines(ctx: CertificateLayoutContext): number {
  return Math.max(4, Math.floor(availableCertificateMessageHeightPx(ctx) / (MIN_FONT_PX * MESSAGE_LINE_HEIGHT)));
}

export function pickCertificateMessageFontSize(message: string, ctx: CertificateLayoutContext): string {
  return solveCertificateTextLayout(message, ctx).messageFontSize;
}

export const CERTIFICATE_MESSAGE_CHARS_PER_LINE = charsPerLineForFontSize(MIN_FONT_PX);
export const CERTIFICATE_SENDER_MAX_CHARS = 400;

export function certificateMessageFits(text: string, ctx: CertificateLayoutContext): boolean {
  return solveCertificateTextLayout(text || '', ctx).fits;
}
